import { FC, useEffect, useState } from 'react';
import { keepPreviousData } from '@tanstack/react-query';
import { OrderData, trpc } from 'src/trpc';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { SortingState } from '@tanstack/react-table';
import { Input } from '@/components/ui/input';
import { DataTable } from 'components/table/DataTable';
import { Loader } from 'components/Loader';
import { useDebounce } from 'components/Debounce';
import { OrderColumns } from './OrderColumns';
import { MainLayout } from 'src/pages/MainLayout';
import { Pagination, Sort } from '@libs/services';
import { useOrganization } from 'src/context/OrganizationContext';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { OrderStatusEnum } from '@libs/schemas/src/order';
import { StatusDisplay } from 'components/StatusDisplay';
import { Button } from '@/components/ui/button';
import { Send } from 'lucide-react';


export const OrderDashboardPage = () => {
  const { mutateAsync: getSignInToken } = trpc.admin.user.adminCreateSignInToken.useMutation();

  return (
    <MainLayout>
      <div className="space-y-6 flex flex-row justify-between">
        <div className="flex flex-row justify-between">
          <h1 className="flex-1 text-3xl font-bold">Orders</h1>
        </div>
        <div className="flex flex-row justify-between">
          <Button
            variant="default"
            onClick={async () => {
              const token = await getSignInToken();
              window.open(
                `${token.customerAppUrl}/checkout/zipcode?adminToken=${token.customToken}`,
                '_blank',
              );
            }}
          >
            <div className="flex flex-row items-center gap-2">
              <Send className="w-4 h-4" />
              <p>Send Quote</p>
            </div>
          </Button>
        </div>
      </div>
      <OrderDashboard />
    </MainLayout>
  );
};

export const OrderDashboard: FC<{ userId?: string }> = ({ userId }) => {
  const [params, setParams] = useSearchParams({
    offset: '0',
    sortDirection: 'asc',
    sortKey: '',
    search: '',
    status: 'all',
  });

  // Use organization context for org-dependent queries
  const { organizationId, isResolved, isOrganizationSwitching, setIsOrganizationSwitching } =
    useOrganization();

  const offset = parseInt(params.get('offset') || '0');
  const limit = 25;
  const { data, isPending, isError, isFetching } = trpc.admin.order.adminListOrders.useQuery(
    {
      limit,
      offset: params.get('offset') ? parseInt(params.get('offset')!) : 0,
      sortDirection: (params.get('sortDirection') as 'asc' | 'desc') || 'asc',
      sortKey: params.get('sortKey') || '',
      search: params.get('search') || '',
      status: (params.get('status') as 'active' | 'inactive' | 'all') || 'all',
      userId: userId,
    },
    {
      placeholderData: keepPreviousData,
      // Only query when organization is resolved
      enabled: isResolved && !!organizationId,
    },
  );

  // Reset organization switching state when fetch completes
  useEffect(() => {
    if (isOrganizationSwitching && !isFetching) {
      setIsOrganizationSwitching(false);
    }
  }, [isOrganizationSwitching, isFetching, setIsOrganizationSwitching]);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 500);

  useEffect(() => {
    params.set('search', debouncedSearch || '');
    params.set('offset', '0');
    setParams(params);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch]);

  const handleStatusFilter = (value: string) => {
    params.set('status', value);
    params.set('offset', '0'); // Reset to first page
    setParams(params);
  };

  // Show loader while org is being resolved or switching
  if (!isResolved || isOrganizationSwitching || (isPending && isFetching)) {
    return <Loader title="Getting Orders" />;
  }

  if (isError || !data) {
    return (
      <div className="flex flex-col items-center justify-center p-8 text-center">
        <h2 className="text-xl font-semibold text-red-800">Error loading orders</h2>
        <p className="mt-2 text-gray-600">
          Please try again later or contact support if the issue persists.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex gap-4 items-center">
        <div className="flex-1">
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search order by customer name, email, phone number, or order ID"
          />
        </div>
        <div className="w-64">
          <Select value={params.get('status') ?? 'all'} onValueChange={handleStatusFilter}>
            <SelectTrigger>
              <SelectValue placeholder="Filter by status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Orders</SelectItem>
              <SelectItem value="active">Active Orders</SelectItem>
              <SelectItem value="inactive">Inactive Orders</SelectItem>

              {/* Divider */}
              <div className="h-px bg-gray-200 my-1" />

              {/* Individual statuses */}
              {Object.values(OrderStatusEnum).map((status) => (
                <SelectItem key={status} value={status}>
                  <div className="flex items-center gap-2">
                    <StatusDisplay status={status} className="text-xs" />
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <OrderDataTable
        orderData={data}
        setParams={(newParams) => {
          params.set('offset', newParams.offset?.toString() || '0');
          params.set('limit', newParams.limit?.toString() || '10');
          params.set('sortDirection', newParams.sortDirection || 'asc');
          params.set('sortKey', newParams.sortKey || '');
          setParams(params);
        }}
        pagination={{ offset, limit }}
        sortingObj={{
          sortDirection: (params.get('sortDirection') as 'asc' | 'desc') || 'asc',
          sortKey: params.get('sortKey') || '',
        }}
      />
    </div>
  );
};

export const OrderDataTable: FC<{
  orderData: OrderData;
  setParams: (params: Pagination & Sort) => void;
  pagination: Pagination;
  sortingObj: Sort;
}> = ({
  orderData,
  setParams,
  pagination: { offset = 0, limit = 10 },
  sortingObj: { sortDirection = 'asc', sortKey = '' },
}) => {
  const navigate = useNavigate();

  const currentPage = offset === 0 ? 1 : offset / limit + 1;

  const [sorting, setSorting] = useState<SortingState>([]);

  useEffect(() => {
    if (sorting.length) {
      setParams({
        offset: 0,
        limit: 10,
        sortDirection: sorting[0].desc ? 'desc' : 'asc',
        sortKey: sorting[0].id,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sorting]);

  // Ensure orderData has the expected structure
  const orders = orderData?.orders || [];
  const total = orderData?.total || 0;

  return (
    <div className="flex flex-col w-full gap-6">
      <DataTable
        total={total}
        resourceName="orders"
        offset={offset}
        limit={limit}
        columns={OrderColumns}
        currentPage={currentPage}
        data={orders}
        sorting={sorting}
        onSortingChange={setSorting}
        hasNextPage={total > offset + limit}
        hasPreviousPage={offset > 0}
        onRowClick={(row) => navigate(`/orders/${row._id}`)}
        onPageChange={(change) => {
          setParams({ offset: offset + change * limit, limit, sortDirection, sortKey });
        }}
        pageCount={Math.ceil(total / limit)}
      />
    </div>
  );
};
